import { Modal } from "@circleco/compass/components/Modal";
import type { ReactNode } from "react";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { toCompassVariant } from "@/react/components/SettingsApp/CompassCompat/modalHelpers";
import type { UIKitVariant } from "@/react/components/SettingsApp/CompassCompat/modalHelpers";

interface UseConfirmModalParams {
  bodyElement?: ReactNode;
  buttonLabel?: string;
  /** Shown only in Compass when no bodyElement is provided. */
  compassDescription?: string;
  /** Shared between Compass and UIKit; use compassDescription for Compass-only copy. */
  description?: string;
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  sentiment?: UIKitVariant;
  title: string;
}

export const useConfirmModal = ({
  title,
  onConfirm,
  bodyElement = null,
  description,
  compassDescription,
  isConfirmDisabled = false,
  buttonLabel,
  sentiment = "danger",
}: UseConfirmModalParams) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const onConfirmButtonClick = () => {
    setIsConfirmModalOpen(true);
  };

  const onConfirmModalClose = () => {
    setIsConfirmModalOpen(false);
  };

  const onConfirmModal = () => {
    setIsConfirmModalOpen(false);
    onConfirm();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onConfirmModalClose();
    }
  };

  const confirmModal = (
    <Modal.Root open={isConfirmModalOpen} onOpenChange={handleOpenChange}>
      <Modal.Content>
        <Modal.Header title={title} showCloseButton />
        <Modal.Body>
          {bodyElement || compassDescription || description}
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: onConfirmModalClose,
            shouldCloseModal: false,
          }}
          primaryAction={{
            label: buttonLabel ?? t("confirm"),
            variant: toCompassVariant(sentiment),
            disabled: isConfirmDisabled,
            onClick: onConfirmModal,
            type: "button",
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );

  return {
    confirmModal,
    isConfirmModalOpen,
    onConfirmButtonClick,
    onConfirmModalClose,
    onConfirmModal,
  };
};
